import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, uploadFile, mediaUrl } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { usePlatformSettings } from '@/lib/platformSettings';
import { trackEvent } from '@/lib/analytics';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, Trash2, ImagePlus, Save, Lock, Plus } from 'lucide-react';
import {
  CONDITIONS_SELL, CONDITIONS_WANTED, PRICE_TYPES, URGENCY_TYPES,
  BUDGET_TYPES, RENT_PRICING_TYPES, AVAILABILITY_TYPES, UNIT_OPTIONS,
} from '@/lib/constants';
import {
  SERVICE_MODE_OPTIONS, TRAVEL_CAPABILITY_OPTIONS, PRICING_MODEL_OPTIONS,
  RESPONSE_TIME_OPTIONS, EMERGENCY_SERVICE_OPTIONS, VISIT_CHARGE_OPTIONS,
} from '@/lib/service_options';
import SEOFieldsBlock from '@/components/marketplace/SEOFieldsBlock';
import MasterMultiSelect from '@/components/marketplace/MasterMultiSelect';

// Grouped unit dropdown reused across Sell / Wanted / Rent listing forms
const UnitSelect = ({ value, onChange, testid = 'post-unit-select' }) => {
  const groups = UNIT_OPTIONS.reduce((acc, u) => {
    (acc[u.group] = acc[u.group] || []).push(u);
    return acc;
  }, {});
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder='Select unit' /></SelectTrigger>
      <SelectContent className='max-h-72'>
        {Object.entries(groups).map(([group, items]) => (
          <React.Fragment key={group}>
            <div className='px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold sticky top-0 bg-popover'>{group}</div>
            {items.map(u => (
              <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
            ))}
          </React.Fragment>
        ))}
      </SelectContent>
    </Select>
  );
};

import { COUNTRIES_OF_ORIGIN } from '@/lib/countries';

const emptyForm = {
  listing_type: 'sell',
  title: '',
  category_id: '',
  subcategory_id: '',
  description: '',
  country: '',
  state: '',
  city: '',
  locality: '',
  country_of_origin: '',
  contact_person: '',
  email: '',
  mobile: '',
  company_name_snapshot: '',
  full_address: '',
  gst_number: '',
  quantity: '',
  unit: '',
  condition: '',
  brand: '',
  model: '',
  year_of_manufacture: '',
  running_hours: '',
  price_type: '',
  asking_price: '',
  currency: 'INR',
  quantity_needed: '',
  preferred_condition: '',
  urgency: '',
  budget_type: '',
  budget_amount: '',
  preferred_supply_location: '',
  quantity_available: '',
  pricing_type: '',
  availability_status: '',
  operator_included: false,
  transport_included: false,
  price_amount: '',
  security_deposit: '',
  available_from: '',
  minimum_rental_period: '',
  maximum_rental_period: '',
  // Service-specific fields
  service_mode: '',
  travel_capability: '',
  pricing_model: '',
  starting_price_inr: '',
  response_time: '',
  emergency_service: '',
  experience_years: '',
  oem_authorized: false,
  authorized_brand: '',
  visit_charge: '',
  certifications: [],
  industries_served: [],
  application_expertise: [],
  video_url: '',
  focus_keyword: '',
  media: [],
};

// Fields common to every listing type, plus the per-type field whitelist.
// Used to strip non-applicable attributes from the submit payload so a
// listing NEVER stores fields belonging to another type (Issue 2 root cause).
const SHARED_FIELDS = [
  'listing_type', 'title', 'category_id', 'subcategory_id', 'description',
  'country', 'state', 'city', 'locality', 'country_of_origin',
  'contact_person', 'email', 'mobile', 'company_name_snapshot', 'full_address',
  'gst_number', 'brand', 'model', 'currency', 'attributes', 'video_url', 'media',
  'slug', 'focus_keyword', 'meta_title', 'meta_description', 'canonical_url',
  'og_image_url', 'og_image_alt', 'og_title', 'og_description', 'schema_type', 'robots_meta',
];
const TYPE_FIELDS = {
  sell: ['quantity', 'unit', 'condition', 'year_of_manufacture', 'running_hours', 'price_type', 'asking_price'],
  wanted: ['quantity_needed', 'preferred_condition', 'urgency', 'budget_type', 'budget_amount', 'preferred_supply_location'],
  rent: ['quantity_available', 'condition', 'pricing_type', 'availability_status', 'operator_included', 'transport_included', 'price_amount', 'security_deposit', 'available_from', 'minimum_rental_period', 'maximum_rental_period'],
  service: ['service_mode', 'travel_capability', 'pricing_model', 'starting_price_inr', 'response_time', 'emergency_service', 'experience_years', 'oem_authorized', 'authorized_brand', 'visit_charge', 'certifications', 'industries_served', 'application_expertise'],
};

const isIndia = (c) => String(c || '').trim().toLowerCase() === 'india';

const Field = ({ label, required, children, hint }) => (
  <div>
    <Label className='text-xs font-medium text-muted-foreground'>{label}{required && <span className='text-danger'> *</span>}</Label>
    {children}
    {hint && <div className='text-[11px] text-muted-foreground mt-1'>{hint}</div>}
  </div>
);

const PostListing = ({ editMode = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // #12: Pull admin-configured minimum image count from platform_settings.
  // Falls back to 2 when the settings hook hasn't loaded yet.
  const { settings } = usePlatformSettings();
  const minImages = Number(settings?.content_rules?.listing_min_images) || 2;
  const { id: editId } = useParams();
  const [searchParams] = useSearchParams();
  // Item 6 — when a moderator opened this editor from the Moderator Panel, we
  // carry the origin + panel tab so we can return them to the exact same tab
  // after saving. Owners have no such params and go to My Listings as before.
  const fromModerator = searchParams.get('from') === 'moderator';
  const modTab = searchParams.get('tab') || 'pending_review';
  const [form, setForm] = useState(emptyForm);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [plan, setPlan] = useState(null);
  const [credits, setCredits] = useState(null);  // {credits_remaining, next_credit, ...}

  // Phase 7C — Category/type lock. Staff (moderator/admin) can still edit
  // these fields to reclassify miscategorized listings.
  const isStaff = user && (user.role === 'moderator' || user.role === 'admin');
  const categoryLocked = editMode && !isStaff;

  useEffect(() => {
    if (user) {
      setForm(f => ({
        ...f,
        contact_person: user.name || '',
        email: user.email || '',
        mobile: user.mobile || '',
        company_name_snapshot: user.company_name || '',
        // Pre-fill the listing country from the user's profile for NEW listings
        // only. In edit mode the listing keeps its own stored country so a later
        // profile-country change never mutates existing listings.
        ...(editMode ? {} : { country: f.country || user.country || '' }),
      }));
    }
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    api.get('/categories', { params: { listing_type: form.listing_type, parent_id: 'null' } })
      .then(r => setCategories(r.data)).catch(() => setCategories([]));
    // In edit mode we must not clear the loaded category/subcategory
    // ids — they are locked (Phase 7C) and identical to the fetched
    // listing anyway.
    if (!editMode) {
      setForm(f => ({ ...f, category_id: '', subcategory_id: '' }));
    }
    // eslint-disable-next-line
  }, [form.listing_type]);

  useEffect(() => {
    if (form.category_id) {
      api.get('/categories', { params: { listing_type: form.listing_type, parent_id: form.category_id } })
        .then(r => setSubcategories(r.data)).catch(() => setSubcategories([]));
    } else setSubcategories([]);
  }, [form.category_id, form.listing_type]);

  useEffect(() => {
    api.get('/locations/states').then(r => setStates(r.data));
  }, []);

  // Fetch current subscription plan for quota enforcement
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const sub = await api.get('/payments/subscription/mine').then(r => r.data);
        if (sub?.plan_id) {
          const plans = await api.get('/payments/plans', { params: { kind: 'subscription' } }).then(r => r.data);
          const p = plans.find(x => x.id === sub.plan_id) || plans.find(x => x.slug === 'free');
          if (p) setPlan(p);
        } else {
          const plans = await api.get('/payments/plans', { params: { kind: 'subscription' } }).then(r => r.data);
          const p = plans.find(x => x.slug === 'free') || plans[0];
          if (p) setPlan(p);
        }
      } catch { /* fall back silently */ }
    })();
  }, [user]);

  // Fetch ad credits — the *next* credit's limits are used INSTEAD of plan quotas if available
  useEffect(() => {
    if (!user || editMode) return;   // don't apply credit limits when editing
    api.get('/ad-credits/mine').then(r => setCredits(r.data)).catch(() => setCredits(null));
  }, [user, editMode]);

  useEffect(() => {
    // India-only city dropdown source; non-India cities are free-text.
    if (isIndia(form.country) && form.state) {
      api.get('/locations/cities', { params: { state: form.state } }).then(r => setCities(r.data));
    } else setCities([]);
  }, [form.state, form.country]);

  // Edit mode — the Category/Subcategory dropdowns only load the currently
  // active top-level (and child) options. If the listing's saved category is
  // hidden/inactive or is a non-top-level id, it wouldn't appear in that list
  // and the locked field rendered blank. Inject the listing's own saved
  // category/subcategory (using its denormalized name) so the real selection
  // is always shown.
  const categoryOptions = useMemo(() => {
    const opts = [...categories];
    if (form.category_id && !opts.some(c => c.id === form.category_id)) {
      opts.unshift({ id: form.category_id, name: form.category_name || 'Current category' });
    }
    return opts;
  }, [categories, form.category_id, form.category_name]);

  const subcategoryOptions = useMemo(() => {
    const opts = [...subcategories];
    if (form.subcategory_id && !opts.some(c => c.id === form.subcategory_id)) {
      opts.unshift({ id: form.subcategory_id, name: form.subcategory_name || 'Current subcategory' });
    }
    return opts;
  }, [subcategories, form.subcategory_id, form.subcategory_name]);

  useEffect(() => {
    if (editMode && editId) {
      api.get(`/listings/${editId}`).then(({ data }) => {
        setForm({ ...emptyForm, ...data });
      });
    }
    // eslint-disable-next-line
  }, [editMode, editId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // If user has ad credits, next credit overrides plan quotas
  const activeCredit = credits?.next_credit;
  const usingCredit = !!activeCredit && !editMode;
  // Editing an EXISTING listing → limits come from the listing's locked
  // creation-time entitlement, never from the owner's current subscription.
  const listingEnt = editMode && form.plan_entitlement ? form.plan_entitlement : null;
  const maxImages = listingEnt ? (listingEnt.max_images_per_listing ?? 2)
    : usingCredit ? (activeCredit.max_images_per_listing ?? 4) : (plan?.max_images_per_listing ?? 2);
  const maxVideos = listingEnt ? (listingEnt.max_videos_per_listing ?? 0)
    : usingCredit ? (activeCredit.max_videos_per_listing ?? 1) : (plan?.max_videos_per_listing ?? 0);
  const maxPdfs = listingEnt ? (listingEnt.max_pdfs_per_listing ?? 0)
    : usingCredit ? (activeCredit.max_pdfs_per_listing ?? 0) : (plan?.max_pdfs_per_listing ?? 0);
  const allowYoutube = listingEnt ? !!listingEnt.allow_youtube_link
    : usingCredit ? !!activeCredit.allow_youtube_link : !!plan?.allow_youtube_link;
  const planName = listingEnt ? (listingEnt.plan_name || 'Free')
    : usingCredit ? activeCredit.plan_name : (plan?.name || 'Free');

  // A lifecycle-expired listing is re-evaluated against the CURRENT plan at
  // renewal, so show that target when it is tighter than the locked snapshot.
  const lifecycleExpired = editMode && form.expires_at
    && new Date(form.expires_at) <= new Date()
    && ['expired', 'pending_review'].includes(form.status);
  const renewalTarget = (lifecycleExpired && plan && listingEnt && (
    (plan.max_images_per_listing ?? 2) < (listingEnt.max_images_per_listing ?? 2)
    || (plan.max_videos_per_listing ?? 0) < (listingEnt.max_videos_per_listing ?? 0)
    || (plan.max_pdfs_per_listing ?? 0) < (listingEnt.max_pdfs_per_listing ?? 0)
    || (!plan.allow_youtube_link && listingEnt.allow_youtube_link)
  )) ? {
    name: plan.name || 'Free',
    images: plan.max_images_per_listing ?? 2,
    videos: plan.max_videos_per_listing ?? 0,
    pdfs: plan.max_pdfs_per_listing ?? 0,
    youtube: !!plan.allow_youtube_link,
  } : null;

  const imageCount = form.media.filter(m => m.kind === 'image').length;
  const videoCount = form.media.filter(m => m.kind === 'video').length;
  const pdfCount = form.media.filter(m => m.kind === 'pdf').length;

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    // Enforce per-plan max
    if (imageCount + files.length > maxImages) {
      toast.error(listingEnt
        ? `This listing was created under the ${planName} plan, which allows ${maxImages} image(s).`
        : `Your ${planName} plan allows only ${maxImages} image(s). Upgrade to add more.`);
      if (!listingEnt) navigate('/pricing');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        if (f.size > 5 * 1024 * 1024) {
          toast.error(`"${f.name}" exceeds 5MB image limit. Please compress and retry.`);
          continue;
        }
        const res = await uploadFile(f, 'image', { context: 'listing_photo' });
        uploaded.push({ ...res, order: form.media.length + uploaded.length });
      }
      if (uploaded.length > 0) {
        set('media', [...form.media, ...uploaded]);
        toast.success(`${uploaded.length} image(s) uploaded`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const handleVideoUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (videoCount + 1 > maxVideos) {
      toast.error(listingEnt
        ? `This listing was created under the ${planName} plan, which allows ${maxVideos} video(s).`
        : `Your ${planName} plan does not allow video uploads. Please upgrade.`);
      if (!listingEnt) navigate('/pricing');
      e.target.value = '';
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error('Video exceeds 15MB limit. Please compress and retry.');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const res = await uploadFile(f, 'video');
      set('media', [...form.media, { ...res, order: form.media.length }]);
      toast.success('Video uploaded');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Video upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handlePdfUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (pdfCount + 1 > maxPdfs) {
      toast.error(listingEnt
        ? `This listing was created under the ${planName} plan, which allows ${maxPdfs} PDF(s).`
        : `Your ${planName} plan does not allow PDF uploads. Please upgrade.`);
      if (!listingEnt) navigate('/pricing');
      e.target.value = '';
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error('PDF exceeds 5MB limit.');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const res = await uploadFile(f, 'pdf');
      set('media', [...form.media, { ...res, order: form.media.length }]);
      toast.success('Brochure uploaded');
    } catch (err) { toast.error(err?.response?.data?.detail || 'PDF upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const removeMedia = (idx) => set('media', form.media.filter((_, i) => i !== idx));

  const submit = async () => {
    // Basic client validation
    const errs = [];
    if (!form.title || form.title.length < 5) errs.push('Title too short');
    if (!form.category_id) errs.push('Please select category');
    if (!form.description || form.description.length < 10) errs.push('Description too short');
    if (!form.country) errs.push('Country required');
    if (isIndia(form.country) && !form.state) errs.push('State required');
    if (!form.city) errs.push('City required');
    if (form.listing_type === 'sell') {
      if (!form.quantity) errs.push('Quantity required');
      if (!form.condition) errs.push('Condition required');
      if (!form.price_type) errs.push('Price type required');
      const imgCount = (form.media || []).filter(m => m.kind === 'image').length;
      if (imgCount < 2) errs.push('At least 2 images are required for Sell');
    }
    if (form.listing_type === 'wanted') {
      if (!form.quantity_needed) errs.push('Quantity needed required');
    }
    if (form.listing_type === 'rent') {
      if (!form.condition) errs.push('Condition required');
      if (!form.quantity_available) errs.push('Quantity available required');
      if (!form.pricing_type) errs.push('Pricing type required');
      if (!form.availability_status) errs.push('Availability status required');
      const imgCount = (form.media || []).filter(m => m.kind === 'image').length;
      if (imgCount < 2) errs.push('At least 2 images are required for Rent');
    }
    if (form.listing_type === 'service') {
      if (!form.service_mode) errs.push('Service mode required');
      if (!form.travel_capability) errs.push('Travel capability required');
      if (!form.pricing_model) errs.push('Pricing model required');
      if (!form.response_time) errs.push('Response time required');
    }
    if (form.video_url) {
      const yt = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|shorts\/|embed\/)|youtu\.be\/)/i;
      if (!yt.test(form.video_url)) errs.push('Video link must be a valid YouTube URL');
      if (!allowYoutube) errs.push(`Your ${planName} plan does not allow YouTube links — upgrade to enable`);
    }

    if (errs.length) { errs.forEach(e => toast.error(e)); return; }

    const payload = { ...form };
    // Numeric coercion
    for (const k of ['asking_price', 'budget_amount', 'price_amount', 'security_deposit', 'year_of_manufacture', 'starting_price_inr', 'experience_years']) {
      if (payload[k] === '' || payload[k] === null) delete payload[k];
      else payload[k] = Number(payload[k]);
    }
    // Convert empty strings to null for optional fields
    Object.keys(payload).forEach(k => {
      if (payload[k] === '') payload[k] = null;
    });
    if (!payload.subcategory_id) delete payload.subcategory_id;

    // Strip any field that does not belong to the selected listing type so
    // non-applicable attributes are never submitted/stored (Issue 2).
    const allowed = new Set([...SHARED_FIELDS, ...(TYPE_FIELDS[payload.listing_type] || [])]);
    Object.keys(payload).forEach((k) => { if (!allowed.has(k)) delete payload[k]; });

    // Phase 7C — category lock. In edit mode strip locked fields for
    // non-staff users so we don't trip the backend's 400 guard when the
    // values are unchanged but present in the payload. Staff keep them
    // to be able to reclassify.
    if (editMode && !isStaff) {
      delete payload.listing_type;
      delete payload.category_id;
      delete payload.subcategory_id;
    }

    setSubmitting(true);
    try {
      let res;
      if (editMode && editId) {
        res = await api.patch(`/listings/${editId}`, payload);
      } else {
        res = await api.post('/listings', payload);
      }
      toast.success(
        editMode
          ? (user && (user.role === 'admin' || user.role === 'moderator')
              ? 'Listing updated (staff override)'
              : 'Listing updated — pending re-review')
          : 'Listing submitted for review'
      );
      // Analytics: fire listing_posted on the CREATE path (not edits) so
      // ad-attribution reports see fresh supply, not re-submissions.
      if (!editMode) {
        trackEvent('listing_posted', {
          listing_id: res?.data?.id,
          listing_type: payload.listing_type,
          category: payload.category_name,
          currency: 'INR',
          value: payload.price || 0,
        });
      }
      if (editMode && fromModerator) {
        navigate(`/moderator?tab=${modTab}`);
      } else {
        navigate(`/dashboard/listings`);
      }
    } catch (e) {
      // Phase 7C — content-moderation rejections come back with a
      // structured detail object. Prefer the top-level message, but also
      // surface any additional reasons in the toast description so the
      // seller can see everything at once.
      const detail = e?.response?.data?.detail;
      const isStructured = detail && typeof detail === 'object';
      // Item 4 — first-listing profile gate rejection from the backend.
      if (isStructured && detail.code === 'profile_incomplete') {
        toast.error(detail.message || 'Complete Your Profile First', {
          description: `Still needed: ${(detail.missing || []).join(', ')}`,
          duration: 8000,
        });
        navigate('/dashboard/profile');
        return;
      }
      const primary = isStructured ? detail.message : (typeof detail === 'string' ? detail : 'Submission failed');
      const extras = isStructured && Array.isArray(detail.reasons)
        ? detail.reasons.slice(1).join('\n')
        : '';
      toast.error(primary, extras ? { description: extras, duration: 8000 } : {});
    } finally { setSubmitting(false); }
  };

  const lt = form.listing_type;

  // Location country options: curated list, plus the current listing/profile
  // country if it isn't already in the list (so it stays selectable).
  const countryOptions = useMemo(() => {
    const c = String(form.country || '').trim();
    return c && !COUNTRIES_OF_ORIGIN.includes(c) ? [c, ...COUNTRIES_OF_ORIGIN] : COUNTRIES_OF_ORIGIN;
  }, [form.country]);

  // Item 4 — address source toggle. Copying the profile address is a one-time
  // copy into the listing fields; the listing keeps its own independent
  // address afterwards (later profile edits never change existing listings).
  const [addressSource, setAddressSource] = useState('different');
  const useProfileAddress = () => {
    setAddressSource('profile');
    setForm(f => ({
      ...f,
      country: user?.country || f.country || '',
      state: user?.state || '',
      city: user?.city || '',
      locality: user?.industrial_area || '',
      full_address: user?.full_address || '',
    }));
  };
  const useDifferentAddress = () => {
    setAddressSource('different');
    setForm(f => ({ ...f, state: '', city: '', locality: '', full_address: '' }));
  };

  // Item 4 — first-listing profile gate (new users only; existing users are
  // never flagged). Mirrors the backend required-field rule.
  const profileMissing = useMemo(() => {
    if (!user) return [];
    const req = [['name', 'Name'], ['mobile', 'Mobile'], ['company_name', 'Company Name'],
                 ['country', 'Country'], ['city', 'City/Location'], ['full_address', 'Full Address']];
    const missing = req.filter(([f]) => !String(user[f] || '').trim()).map(([, l]) => l);
    if (String(user.country || '').trim().toLowerCase() === 'india' && !String(user.state || '').trim()) {
      missing.push('State/Province/Region');
    }
    return missing;
  }, [user]);
  const showProfileGate = !editMode && user?.profile_gate_required && profileMissing.length > 0;

  if (showProfileGate) {
    return (
      <div className='indus-container py-12 max-w-xl' data-testid='profile-gate'>
        <Card className='p-8 text-center space-y-4'>
          <h1 className='text-2xl font-semibold tracking-tight'>Complete Your Profile First</h1>
          <p className='text-muted-foreground text-sm'>
            Before posting your first listing, please complete your profile so buyers can trust and reach you.
          </p>
          <div className='text-left bg-secondary/50 rounded-lg p-4'>
            <div className='text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2'>Still needed</div>
            <ul className='text-sm list-disc pl-5 space-y-1' data-testid='profile-gate-missing'>
              {profileMissing.map(m => <li key={m}>{m}</li>)}
            </ul>
          </div>
          <Button onClick={() => navigate('/dashboard/profile')} data-testid='profile-gate-cta'>
            Complete my profile
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className='indus-container py-8'>
      <div className='flex items-end justify-between mb-6'>
        <div>
          <h1 className='text-2xl sm:text-3xl font-semibold tracking-tight'>{editMode ? 'Edit listing' : 'Post a new listing'}</h1>
          <p className='text-muted-foreground text-sm'>All submissions are reviewed by moderators within 24 hours.</p>
        </div>
        {categoryLocked && (
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={() => navigate('/post')}
            data-testid='post-new-listing-shortcut'
          >
            <Plus className='h-4 w-4 mr-1' /> Post a new listing
          </Button>
        )}
      </div>

      {categoryLocked && (
        <Card className='p-4 mb-6 border-amber-200 bg-amber-50/70' data-testid='category-lock-banner'>
          <div className='flex items-start gap-3'>
            <Lock className='h-5 w-5 text-amber-600 shrink-0 mt-0.5' />
            <div className='text-sm'>
              <div className='font-semibold text-amber-900'>Category is locked for this listing</div>
              <p className='text-amber-800/90 mt-0.5 text-xs'>
                You can freely update pricing, description, images, and location. Listing type,
                category and subcategory are locked once posted to keep search results consistent
                for buyers. To list in a different category, please create a new listing.
              </p>
            </div>
          </div>
        </Card>
      )}

      {usingCredit && (
        <Card className='p-4 mb-6 border-accent/40 bg-accent/5' data-testid='post-using-credit-banner'>
          <div className='flex items-start gap-3'>
            <div className='shrink-0 h-9 w-9 rounded-full bg-accent/15 flex items-center justify-center'>
              <span className='text-accent font-semibold text-sm'>🎫</span>
            </div>
            <div className='text-sm flex-1'>
              <div className='font-semibold'>
                Using an ad credit from your <span className='text-accent'>{activeCredit.plan_name}</span>
                <span className='text-muted-foreground font-normal'> · {credits.credits_remaining} credit(s) remaining</span>
              </div>
              <div className='text-muted-foreground mt-0.5 text-xs'>
                This ad gets {activeCredit.max_images_per_listing} images + {activeCredit.max_videos_per_listing} video{activeCredit.allow_youtube_link ? ' + 1 YouTube link' : ''}
                {' — plus auto-Featured for '}{activeCredit.featured_days_per_ad} day(s) once approved.
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card className='p-6 mb-6'>
        <div className='mb-3 font-semibold flex items-center gap-2'>
          1. Listing type
          {categoryLocked && <Lock className='h-3.5 w-3.5 text-muted-foreground' aria-label='Locked' />}
        </div>
        <Tabs value={lt} onValueChange={(v) => !categoryLocked && set('listing_type', v)}>
          <TabsList data-testid='post-listing-type-tabs'>
            <TabsTrigger value='sell' disabled={categoryLocked} data-testid='post-tab-sell'>Sell</TabsTrigger>
            <TabsTrigger value='wanted' disabled={categoryLocked} data-testid='post-tab-wanted'>Wanted</TabsTrigger>
            <TabsTrigger value='rent' disabled={categoryLocked} data-testid='post-tab-rent'>Rent</TabsTrigger>
            <TabsTrigger value='service' disabled={categoryLocked} data-testid='post-tab-service'>Service</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className='text-xs text-muted-foreground mt-2'>
          {lt === 'sell' && 'Sell: List an industrial item you want to sell (machinery, spares, materials).'}
          {lt === 'wanted' && 'Wanted: Post a buy requirement so suppliers can send offers.'}
          {lt === 'rent' && 'Rent: List industrial equipment available for rent.'}
          {lt === 'service' && 'Service: Offer an industrial service — AMC, fabrication, calibration, installation, engineering or consulting.'}
        </div>
      </Card>

      <Card className='p-6 mb-6 space-y-4'>
        <div className='font-semibold'>2. Basic details</div>
        <div className='grid md:grid-cols-2 gap-4'>
          <Field label={lt === 'wanted' ? 'Requirement Title' : lt === 'rent' ? 'Rental Title' : 'Listing Title'} required>
            <Input data-testid='post-title' value={form.title} onChange={(e) => set('title', e.target.value)} placeholder='e.g. Used HAAS VF-2 CNC Vertical Machining Center' />
          </Field>
          <div className='grid grid-cols-2 gap-3'>
            <Field label={<span className='inline-flex items-center gap-1'>Category {categoryLocked && <Lock className='h-3 w-3 text-muted-foreground' />}</span>} required>
              <Select value={form.category_id} onValueChange={(v) => set('category_id', v)} disabled={categoryLocked}>
                <SelectTrigger data-testid='post-category-select'><SelectValue placeholder='Select' /></SelectTrigger>
                <SelectContent>{categoryOptions.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={<span className='inline-flex items-center gap-1'>Subcategory {categoryLocked && <Lock className='h-3 w-3 text-muted-foreground' />}</span>}>
              <Select value={form.subcategory_id} onValueChange={(v) => set('subcategory_id', v)} disabled={categoryLocked || !form.category_id}>
                <SelectTrigger data-testid='post-subcategory-select'><SelectValue placeholder='Select' /></SelectTrigger>
                <SelectContent>{subcategoryOptions.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
        </div>
        <Field label={lt === 'wanted' ? 'Requirement Description' : 'Description'} required>
          <Textarea rows={5} data-testid='post-description' value={form.description} onChange={(e) => set('description', e.target.value)} placeholder='Describe key specs, condition, usage, and any details buyers should know.' />
        </Field>
      </Card>

      {lt === 'sell' && (
        <Card className='p-6 mb-6 space-y-4'>
          <div className='font-semibold'>3. Sell details</div>
          <div className='grid md:grid-cols-3 gap-4'>
            <Field label='Quantity' required><Input data-testid='post-quantity' value={form.quantity} onChange={(e) => set('quantity', e.target.value)} placeholder='e.g. 1' /></Field>
            <Field label='Unit'>
              <UnitSelect value={form.unit} onChange={(v) => set('unit', v)} testid='post-unit-select-sell' />
            </Field>
            <Field label='Condition' required>
              <Select value={form.condition} onValueChange={(v) => set('condition', v)}>
                <SelectTrigger data-testid='post-condition-select'><SelectValue placeholder='Select' /></SelectTrigger>
                <SelectContent>{CONDITIONS_SELL.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label='Brand'><Input value={form.brand} onChange={(e) => set('brand', e.target.value)} /></Field>
            <Field label='Model'><Input value={form.model} onChange={(e) => set('model', e.target.value)} /></Field>
            <Field label='Country of origin'>
              <Select value={form.country_of_origin || ''} onValueChange={(v) => set('country_of_origin', v === '__none__' ? '' : v)}>
                <SelectTrigger data-testid='post-country-of-origin-select'><SelectValue placeholder='Where manufactured?' /></SelectTrigger>
                <SelectContent className='max-h-72'>
                  <SelectItem value='__none__'>Not sure / not applicable</SelectItem>
                  {COUNTRIES_OF_ORIGIN.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label='Year of manufacture'><Input type='number' value={form.year_of_manufacture} onChange={(e) => set('year_of_manufacture', e.target.value)} /></Field>
            <Field label='Running Hours / Usage'><Input value={form.running_hours} onChange={(e) => set('running_hours', e.target.value)} /></Field>
            <Field label='Price Type' required>
              <Select value={form.price_type} onValueChange={(v) => set('price_type', v)}>
                <SelectTrigger data-testid='post-price-type-select'><SelectValue placeholder='Select' /></SelectTrigger>
                <SelectContent>{PRICE_TYPES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label='Asking Price (₹)'><Input type='number' data-testid='post-asking-price' value={form.asking_price} onChange={(e) => set('asking_price', e.target.value)} /></Field>
          </div>
        </Card>
      )}

      {lt === 'wanted' && (
        <Card className='p-6 mb-6 space-y-4'>
          <div className='font-semibold'>3. Wanted details</div>
          <div className='grid md:grid-cols-3 gap-4'>
            <Field label='Quantity Needed' required><Input data-testid='post-quantity-needed' value={form.quantity_needed} onChange={(e) => set('quantity_needed', e.target.value)} /></Field>
            <Field label='Unit'>
              <UnitSelect value={form.unit} onChange={(v) => set('unit', v)} testid='post-unit-select-wanted' />
            </Field>
            <Field label='Preferred Condition'>
              <Select value={form.preferred_condition} onValueChange={(v) => set('preferred_condition', v)}>
                <SelectTrigger data-testid='post-preferred-condition'><SelectValue placeholder='Select' /></SelectTrigger>
                <SelectContent>{CONDITIONS_WANTED.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label='Preferred Brand'><Input value={form.brand} onChange={(e) => set('brand', e.target.value)} /></Field>
            <Field label='Preferred Model'><Input value={form.model} onChange={(e) => set('model', e.target.value)} /></Field>
            <Field label='Preferred country of origin'>
              <Select value={form.country_of_origin || ''} onValueChange={(v) => set('country_of_origin', v === '__none__' ? '' : v)}>
                <SelectTrigger data-testid='post-wanted-country-of-origin-select'><SelectValue placeholder='Any' /></SelectTrigger>
                <SelectContent className='max-h-72'>
                  <SelectItem value='__none__'>Any country</SelectItem>
                  {COUNTRIES_OF_ORIGIN.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label='Preferred Supply Location'><Input value={form.preferred_supply_location} onChange={(e) => set('preferred_supply_location', e.target.value)} /></Field>
            <Field label='Urgency'>
              <Select value={form.urgency} onValueChange={(v) => set('urgency', v)}>
                <SelectTrigger data-testid='post-urgency-select'><SelectValue placeholder='Select' /></SelectTrigger>
                <SelectContent>{URGENCY_TYPES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label='Budget Type'>
              <Select value={form.budget_type} onValueChange={(v) => set('budget_type', v)}>
                <SelectTrigger data-testid='post-budget-type-select'><SelectValue placeholder='Select' /></SelectTrigger>
                <SelectContent>{BUDGET_TYPES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label='Budget Amount (₹)'><Input type='number' value={form.budget_amount} onChange={(e) => set('budget_amount', e.target.value)} /></Field>
          </div>
        </Card>
      )}

      {lt === 'rent' && (
        <Card className='p-6 mb-6 space-y-4'>
          <div className='font-semibold'>3. Rent details</div>
          <div className='grid md:grid-cols-3 gap-4'>
            <Field label='Quantity Available' required><Input data-testid='post-quantity-available' value={form.quantity_available} onChange={(e) => set('quantity_available', e.target.value)} /></Field>
            <Field label='Unit'>
              <UnitSelect value={form.unit} onChange={(v) => set('unit', v)} testid='post-unit-select-rent' />
            </Field>
            <Field label='Condition' required>
              <Select value={form.condition} onValueChange={(v) => set('condition', v)}>
                <SelectTrigger data-testid='post-rent-condition-select'><SelectValue placeholder='Select' /></SelectTrigger>
                <SelectContent>{CONDITIONS_SELL.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label='Availability Status' required>
              <Select value={form.availability_status} onValueChange={(v) => set('availability_status', v)}>
                <SelectTrigger data-testid='post-availability-status'><SelectValue placeholder='Select' /></SelectTrigger>
                <SelectContent>{AVAILABILITY_TYPES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label='Brand'><Input value={form.brand} onChange={(e) => set('brand', e.target.value)} /></Field>
            <Field label='Model'><Input value={form.model} onChange={(e) => set('model', e.target.value)} /></Field>
            <Field label='Country of origin'>
              <Select value={form.country_of_origin || ''} onValueChange={(v) => set('country_of_origin', v === '__none__' ? '' : v)}>
                <SelectTrigger data-testid='post-rent-country-of-origin-select'><SelectValue placeholder='Where manufactured?' /></SelectTrigger>
                <SelectContent className='max-h-72'>
                  <SelectItem value='__none__'>Not sure / not applicable</SelectItem>
                  {COUNTRIES_OF_ORIGIN.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label='Pricing Type' required>
              <Select value={form.pricing_type} onValueChange={(v) => set('pricing_type', v)}>
                <SelectTrigger data-testid='post-pricing-type-select'><SelectValue placeholder='Select' /></SelectTrigger>
                <SelectContent>{RENT_PRICING_TYPES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label='Price Amount (₹)'><Input type='number' value={form.price_amount} onChange={(e) => set('price_amount', e.target.value)} /></Field>
            <Field label='Security Deposit (₹)'><Input type='number' value={form.security_deposit} onChange={(e) => set('security_deposit', e.target.value)} /></Field>
            <Field label='Available From'><Input type='date' value={form.available_from || ''} onChange={(e) => set('available_from', e.target.value)} /></Field>
            <Field label='Minimum rental period'><Input value={form.minimum_rental_period} onChange={(e) => set('minimum_rental_period', e.target.value)} placeholder='e.g. 15 days' /></Field>
            <Field label='Maximum rental period'><Input value={form.maximum_rental_period} onChange={(e) => set('maximum_rental_period', e.target.value)} placeholder='e.g. 6 months' /></Field>
            <Field label='Operator included?'>
              <Select value={form.operator_included ? 'yes' : 'no'} onValueChange={(v) => set('operator_included', v === 'yes')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value='no'>No</SelectItem><SelectItem value='yes'>Yes</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label='Transport included?'>
              <Select value={form.transport_included ? 'yes' : 'no'} onValueChange={(v) => set('transport_included', v === 'yes')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value='no'>No</SelectItem><SelectItem value='yes'>Yes</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
        </Card>
      )}

      {lt === 'service' && (
        <Card className='p-6 mb-6 space-y-4' data-testid='post-service-card'>
          <div className='font-semibold'>3. Service details</div>
          <div className='grid md:grid-cols-3 gap-4'>
            <Field label='Service Mode' required>
              <Select value={form.service_mode} onValueChange={(v) => set('service_mode', v)}>
                <SelectTrigger data-testid='post-service-mode'><SelectValue placeholder='Where do you deliver?' /></SelectTrigger>
                <SelectContent>
                  {SERVICE_MODE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label='Travel Capability' required hint='How far can you travel?'>
              <Select value={form.travel_capability} onValueChange={(v) => set('travel_capability', v)}>
                <SelectTrigger data-testid='post-service-travel'><SelectValue placeholder='Select' /></SelectTrigger>
                <SelectContent>
                  {TRAVEL_CAPABILITY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label='Response Time' required>
              <Select value={form.response_time} onValueChange={(v) => set('response_time', v)}>
                <SelectTrigger data-testid='post-service-response'><SelectValue placeholder='How fast?' /></SelectTrigger>
                <SelectContent>
                  {RESPONSE_TIME_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label='Pricing Model' required>
              <Select value={form.pricing_model} onValueChange={(v) => set('pricing_model', v)}>
                <SelectTrigger data-testid='post-service-pricing'><SelectValue placeholder='Select' /></SelectTrigger>
                <SelectContent>
                  {PRICING_MODEL_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label='Starting Price (₹)' hint='Optional — helps buyers gauge'>
              <Input type='number' data-testid='post-service-starting-price' value={form.starting_price_inr} onChange={(e) => set('starting_price_inr', e.target.value)} placeholder='e.g. 1500' />
            </Field>
            <Field label='Emergency Service' required>
              <Select value={form.emergency_service || 'no'} onValueChange={(v) => set('emergency_service', v)}>
                <SelectTrigger data-testid='post-service-emergency'><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EMERGENCY_SERVICE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label='Years of Experience'>
              <Input type='number' data-testid='post-service-experience' value={form.experience_years} onChange={(e) => set('experience_years', e.target.value)} placeholder='e.g. 7' />
            </Field>
            <Field label='Visit Charge'>
              <Select value={form.visit_charge || ''} onValueChange={(v) => set('visit_charge', v)}>
                <SelectTrigger data-testid='post-service-visit-charge'><SelectValue placeholder='Free / Paid / Included' /></SelectTrigger>
                <SelectContent>
                  {VISIT_CHARGE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label='OEM Authorized?'>
              <Select value={form.oem_authorized ? 'yes' : 'no'} onValueChange={(v) => set('oem_authorized', v === 'yes')}>
                <SelectTrigger data-testid='post-service-oem'><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value='no'>No</SelectItem><SelectItem value='yes'>Yes</SelectItem></SelectContent>
              </Select>
            </Field>
            {form.oem_authorized && (
              <Field label='Authorized Brand(s)' hint='e.g. Fanuc, Siemens'>
                <Input data-testid='post-service-authorized-brand' value={form.authorized_brand} onChange={(e) => set('authorized_brand', e.target.value)} placeholder='Comma-separated list' />
              </Field>
            )}
          </div>

          <MasterMultiSelect
            testid='post-service-certifications'
            label='Certifications'
            hint='Multi-select.'
            kind='certifications'
            value={form.certifications || []}
            onChange={(v) => set('certifications', v)}
          />
          <MasterMultiSelect
            testid='post-service-industries'
            label='Industries Served'
            hint='Which industries do you work with?'
            kind='industries'
            value={form.industries_served || []}
            onChange={(v) => set('industries_served', v)}
          />
          <MasterMultiSelect
            testid='post-service-applications'
            label='Application Expertise'
            hint='Specific problem areas you specialise in (e.g. Packaging, HVAC, Water Treatment).'
            kind='applications'
            value={form.application_expertise || []}
            onChange={(v) => set('application_expertise', v)}
          />
        </Card>
      )}

      <Card className='p-6 mb-6 space-y-4'>
        <div className='font-semibold'>4. Location &amp; Contact details</div>
        <div className='flex flex-wrap gap-2' data-testid='post-address-source'>
          <Button type='button' size='sm' variant={addressSource === 'profile' ? 'default' : 'outline'}
            onClick={useProfileAddress} data-testid='post-use-profile-address'>Use My Profile Address</Button>
          <Button type='button' size='sm' variant={addressSource === 'different' ? 'default' : 'outline'}
            onClick={useDifferentAddress} data-testid='post-use-different-address'>Use a Different Address</Button>
        </div>
        <div className='grid md:grid-cols-4 gap-4'>
          <Field label='Country' required>
            <Select value={form.country || ''} onValueChange={(v) => setForm(f => ({ ...f, country: v, state: '', city: '' }))}>
              <SelectTrigger data-testid='post-country-select'><SelectValue placeholder='Select country' /></SelectTrigger>
              <SelectContent className='max-h-72'>
                {countryOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          {isIndia(form.country) ? (
            <Field label='State' required>
              <Select value={form.state} onValueChange={(v) => { set('state', v); set('city', ''); }}>
                <SelectTrigger data-testid='post-state-select'><SelectValue placeholder='Select' /></SelectTrigger>
                <SelectContent>{states.map(s => <SelectItem key={s.state} value={s.state}>{s.state}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          ) : (
            <Field label='State / Province / Region'>
              <Input data-testid='post-state-input' value={form.state || ''} onChange={(e) => set('state', e.target.value)} placeholder='e.g. Guangdong' />
            </Field>
          )}
          {isIndia(form.country) ? (
            <Field label='City' required>
              <Select value={form.city} onValueChange={(v) => set('city', v)} disabled={!form.state}>
                <SelectTrigger data-testid='post-city-select'><SelectValue placeholder='Select' /></SelectTrigger>
                <SelectContent>{cities.map(c => <SelectItem key={c.id} value={c.city}>{c.city}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          ) : (
            <Field label='City' required>
              <Input data-testid='post-city-input' value={form.city || ''} onChange={(e) => set('city', e.target.value)} placeholder='e.g. Shenzhen' />
            </Field>
          )}
          <Field label='Industrial Area / Locality'>
            <Input data-testid='post-locality' value={form.locality || ''} onChange={(e) => set('locality', e.target.value)} placeholder='e.g. MIDC Chakan' />
          </Field>
          <div className='md:col-span-4'>
            <Field label='Full Address (private)'><Input data-testid='post-full-address' value={form.full_address} onChange={(e) => set('full_address', e.target.value)} /></Field>
          </div>
        </div>
        <div className='text-xs text-muted-foreground font-normal pt-2'>Contact details below are internal only — never shown publicly.</div>
        <div className='grid md:grid-cols-2 gap-4'>
          <Field label='Contact person'><Input value={form.contact_person} onChange={(e) => set('contact_person', e.target.value)} /></Field>
          <Field label='Mobile'><Input value={form.mobile} onChange={(e) => set('mobile', e.target.value)} /></Field>
          <Field label='Email'><Input value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
          <Field label='Company name (private)'><Input value={form.company_name_snapshot} onChange={(e) => set('company_name_snapshot', e.target.value)} /></Field>
          <Field label='GST Number (private)'><Input value={form.gst_number} onChange={(e) => set('gst_number', e.target.value)} /></Field>
        </div>
      </Card>

      <Card className='p-6 mb-6'>
        <div className='flex items-start justify-between gap-3 flex-wrap mb-1'>
          <div>
            <div className='font-semibold'>5. Media</div>
            <div className='text-xs text-muted-foreground'>First image is the main image.</div>
          </div>
          {(plan || listingEnt) && (
            <div className='text-xs rounded-md border px-2 py-1 bg-secondary/50' data-testid='post-plan-quota'>
              <span className='font-semibold'>{planName} plan</span>
              {listingEnt && <span className='text-muted-foreground'> (locked to this listing)</span>}
              <span className='text-muted-foreground'>
                {' '}· Images {imageCount}/{maxImages} · Videos {videoCount}/{maxVideos} · PDFs {pdfCount}/{maxPdfs}
              </span>
              {!listingEnt && (imageCount >= maxImages || videoCount >= maxVideos || pdfCount >= maxPdfs) && (
                <button
                  onClick={() => navigate('/pricing')}
                  className='ml-2 text-primary hover:underline'
                  data-testid='post-upgrade-cta'
                >
                  Upgrade →
                </button>
              )}
            </div>
          )}
        </div>
        {renewalTarget && (
          <div className='mt-2 text-xs rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 px-2 py-1.5' data-testid='post-renewal-quota-hint'>
            This listing has finished its 60-day run. To renew it, the media must fit your current{' '}
            <span className='font-semibold'>{renewalTarget.name}</span> plan: {renewalTarget.images} image(s),{' '}
            {renewalTarget.videos} video(s), {renewalTarget.pdfs} PDF(s)
            {renewalTarget.youtube ? '' : ', no YouTube link'}.
          </div>
        )}
        {['sell', 'rent'].includes(lt) && (
          <div className='mt-2 text-xs text-muted-foreground' data-testid='post-min-images-hint'>
            <span className={imageCount >= minImages ? 'text-[hsl(var(--verified))]' : 'text-[hsl(var(--danger))] font-medium'}>
              Minimum {minImages} {minImages === 1 ? 'image' : 'images'} required for {lt.toUpperCase()} listings.
            </span>{' '}
            You have {imageCount}.
          </div>
        )}
        <div className='mt-2 text-[11px] text-muted-foreground' data-testid='post-image-recommendation'>
          Recommended: <strong>1200 × 900 px</strong> (4:3), max 5 MB. Photos are shown in full (fitted, never cropped or stretched) — the whole machine/product stays visible.
        </div>
        <div className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mt-4'>
          {form.media.map((m, i) => (
            <div key={i} className='relative aspect-square rounded-lg border bg-secondary overflow-hidden group'>
              {m.kind === 'image' ? (
                <img src={mediaUrl(m.thumb_url || m.public_url)} alt='' className='w-full h-full object-contain' />
              ) : m.kind === 'video' ? (
                <div className='w-full h-full flex items-center justify-center text-xs text-muted-foreground'>Video</div>
              ) : (
                <div className='w-full h-full flex items-center justify-center text-xs text-muted-foreground'>PDF</div>
              )}
              <button type='button' onClick={() => removeMedia(i)} className='absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center' data-testid={`post-remove-media-${i}`}>
                <Trash2 className='h-3 w-3' />
              </button>
              {i === 0 && m.kind === 'image' && <span className='absolute bottom-1 left-1 text-[10px] px-1.5 py-0.5 rounded bg-primary text-primary-foreground'>Main</span>}
            </div>
          ))}
          <label
            className={`aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 text-xs ${
              imageCount >= maxImages ? 'opacity-50 cursor-not-allowed bg-muted' : 'cursor-pointer hover:bg-secondary text-muted-foreground'
            }`}
            data-testid='post-add-images-label'
          >
            <ImagePlus className='h-5 w-5' />
            {imageCount >= maxImages ? `Max ${maxImages} reached` : 'Add images'}
            <input
              data-testid='post-image-upload'
              type='file'
              multiple
              accept='image/*'
              className='hidden'
              onChange={handleImageUpload}
              disabled={uploading || imageCount >= maxImages}
            />
          </label>
        </div>
        <div className='mt-4 flex flex-wrap gap-3'>
          <label
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm ${
              videoCount >= maxVideos ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-secondary'
            }`}
            title={videoCount >= maxVideos ? `Your ${planName} plan does not allow more videos` : ''}
          >
            <Upload className='h-4 w-4' /> Add video (max 15MB){maxVideos === 0 ? ' · Upgrade to enable' : ''}
            <input type='file' accept='video/*' className='hidden' onChange={handleVideoUpload} disabled={uploading || videoCount >= maxVideos} />
          </label>
          <label
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm ${
              pdfCount >= maxPdfs ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-secondary'
            }`}
            title={pdfCount >= maxPdfs ? `Your ${planName} plan does not allow more PDFs` : ''}
          >
            <Upload className='h-4 w-4' /> Add brochure/PDF (max 5MB){maxPdfs === 0 ? ' · Upgrade to enable' : ''}
            <input type='file' accept='.pdf' className='hidden' onChange={handlePdfUpload} disabled={uploading || pdfCount >= maxPdfs} />
          </label>
        </div>
        <div className='mt-4'>
          <Field
            label={`YouTube video link${allowYoutube ? '' : ' (Upgrade required)'}`}
            hint={allowYoutube ? 'Paste a public YouTube video URL to embed on your listing detail page.' : `Your ${planName} plan does not include YouTube links. Upgrade to enable.`}
          >
            <Input
              data-testid='post-video-url'
              value={form.video_url || ''}
              disabled={!allowYoutube}
              placeholder={allowYoutube ? 'https://www.youtube.com/watch?v=...' : 'Upgrade your plan to enable YouTube video links'}
              onChange={(e) => set('video_url', e.target.value)}
            />
          </Field>
        </div>
        {/* SEO fields — restricted to staff. Sellers don't need to think
            about SEO; the platform generates meta from title/description/
            category. Admins & moderators editing on the seller's behalf can
            override everything via this block. See user correction to 7E. */}
        {isStaff && (
          <>
            <div className='mt-4'>
              <Field
                label='SEO focus keyword (staff-only)'
                hint='Only visible to admins/moderators. Sets the primary keyword this listing should rank for.'
              >
                <Input
                  data-testid='post-focus-keyword'
                  value={form.focus_keyword || ''}
                  placeholder='e.g. used CNC lathe'
                  maxLength={120}
                  onChange={(e) => set('focus_keyword', e.target.value)}
                />
              </Field>
            </div>
            <div className='mt-4'>
              <SEOFieldsBlock
                values={form}
                onChange={(patch) => setForm(f => ({ ...f, ...patch }))}
                allowAdvanced
                compact
                testIdPrefix='post-seo'
                contentType='listing'
              />
            </div>
          </>
        )}
      </Card>

      <div className='sticky bottom-0 bg-background/95 backdrop-blur border-t py-4 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 mt-6'>
        <div className='max-w-none flex flex-wrap gap-3 justify-end'>
          <Button variant='outline' onClick={() => navigate(-1)} data-testid='post-cancel-button'>Cancel</Button>
          <Button onClick={submit} disabled={submitting || uploading} data-testid='post-submit-button'>
            <Save className='h-4 w-4 mr-1.5' /> {submitting ? 'Submitting…' : editMode ? 'Update listing' : 'Submit for review'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PostListing;
